// src/pages/Messages.js - task list section 776. Messages between a member and the officers who represent him: his Local
// Chairperson and his General Chairperson (Bob 2026-10-22: messaging on the platform is the primary channel; e-mail only
// says that something is waiting). A member sees his one conversation and writes in it. An officer sees the conversations
// of the members he represents - the Local Chairperson by his territories, the General Chairperson by his committee - and
// answers. Who reads what is firestore.rules and src\data\memberMessages.js; every write goes through the callables.
// The reads are shaped the way the rules can prove them (one query per territory, as the notification centre does).
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, doc, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MAX_BODY, sideWords } from '../data/memberMessages';
import { capScopes } from '../data/scopeCap';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', mine: '#14304F' };
const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return String(iso || ''); } };

function threadQueries(user) {
  const base = collection(db, 'messageThreads');
  if (!user || !user.uid) return [];
  if (user.isAdmin) return [query(base)];
  if (user.role === 'general_chair' && user.committee) return [query(base, where('committee', '==', user.committee))];
  if (user.role === 'local_chair' && Array.isArray(user.scopes)) return capScopes(user).scopes.map((sk) => query(base, where('scopeKey', '==', sk)));
  return [];
}
function messageQuery(user, thread) {
  const base = collection(db, 'messageThreads', thread.memberUid, 'messages');
  if (user.isAdmin) return query(base);
  if (thread.memberUid === user.uid) return query(base, where('memberUid', '==', user.uid));
  if (user.role === 'general_chair') return query(base, where('committee', '==', user.committee));
  return query(base, where('scopeKey', '==', thread.scopeKey));
}

export default function Messages({ user }) {
  const officer = !!(user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair'));
  const [threads, setThreads] = useState(null);     // officer: the list; member: [his thread] or []
  const [openUid, setOpenUid] = useState(null);
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  // The threads this account may read.
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    if (!officer) {
      return onSnapshot(doc(db, 'messageThreads', user.uid),
        (snap) => { setThreads(snap.exists() ? [Object.assign({ id: snap.id }, snap.data())] : []); setOpenUid(user.uid); },
        () => { setThreads([]); setOpenUid(user.uid); });
    }
    const qs = threadQueries(user);
    const parts = qs.map(() => null);
    const unsubs = qs.map((q, i) => onSnapshot(q, (snap) => {
      parts[i] = snap.docs.map((d) => Object.assign({ id: d.id }, d.data()));
      if (parts.every((p) => p !== null)) {
        const all = {}; parts.flat().forEach((t) => { all[t.id] = t; });
        setThreads(Object.values(all).sort((a, b) => String(b.lastMessageAt || '').localeCompare(String(a.lastMessageAt || ''))));
      }
    }, (e) => setErr('The conversations could not be read: ' + (e && e.code ? e.code : 'error') + '. That is not an empty list.')));
    return () => unsubs.forEach((u) => u());
  }, [user, officer]);

  const open = useMemo(() => (threads || []).find((t) => t.memberUid === openUid || t.id === openUid) || null, [threads, openUid]);

  // The messages of the open thread, oldest first; opening it records that this account has read it.
  useEffect(() => {
    setMessages(null);
    if (!open) { setMessages([]); return undefined; }
    const unsub = onSnapshot(messageQuery(user, open), (snap) => {
      setMessages(snap.docs.map((d) => Object.assign({ id: d.id }, d.data())).sort((a, b) => String(a.sentAt || '').localeCompare(String(b.sentAt || ''))));
    }, (e) => setErr('The messages could not be read: ' + (e && e.code ? e.code : 'error') + '.'));
    httpsCallable(getFunctions(), 'markThreadRead')({ threadUid: open.memberUid }).catch(() => {});
    return unsub;
  }, [open && open.memberUid, open && open.count]);   // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setErr(''); setNote('');
    const body = draft.trim();
    if (!body) { setErr('Write your message first.'); return; }
    setBusy(true);
    try {
      const res = await httpsCallable(getFunctions(), 'memberMessage')(officer ? { threadUid: open.memberUid, body } : { body });
      const d = (res && res.data) || {};
      setDraft('');
      setNote('Sent. ' + (d.to && d.to.length ? 'On the platform to ' + d.to.map((a) => (a === 'claimant' ? 'the member' : a === 'local_chair' ? 'the Local Chairperson' : 'the General Chairperson')).join(' and ') + '. ' : '')
        + (d.emailOn ? (d.emailed && d.emailed.length ? 'An e-mail notice, with no details, was also sent.' : '') : 'E-mail notices are not switched on yet; the message waits on the platform.'));
    } catch (e) {
      setErr((e && e.message) || 'The message could not be sent. Check your connection and try again.');
    } finally { setBusy(false); }
  };

  const canWrite = officer ? !!open : true;   // a member always may try; the server says plainly if his account has no office to write to
  const readNames = () => {
    if (!open || !open.lastMessageAt) return '';
    const others = Object.entries(open.readBy || {}).filter(([uid, t]) => uid !== user.uid && String(t) >= String(open.lastMessageAt));
    if (!others.length) return 'Not yet read by the other side since the last message.';
    return 'Read since the last message by ' + (officer ? (others.some(([uid]) => uid === open.memberUid) ? 'the member' : 'another officer') : (others.length === 1 ? 'one of your officers' : others.length + ' of your officers')) + '.';
  };

  return (
    <div style={{ color: C.text, maxWidth: 900 }}>
      <h1 style={{ marginTop: 0, fontSize: 20 }}>Messages</h1>
      <div style={{ fontSize: 13, color: C.dim, marginBottom: 12, lineHeight: 1.6 }}>
        {officer
          ? 'Conversations with the members you represent. A member opens a conversation; you answer it. Every message is kept; none is edited or deleted.'
          : 'Write to your Local Chairperson and your General Chairperson. Only they, and the administrator, can read this conversation. Every message is kept; none is edited or deleted. For a matter on a claim, use the claim\'s own question box as well.'}
      </div>
      {err && <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {threads === null && <div style={{ color: C.dim }}>Loading...</div>}

      {officer && threads && (
        <div style={{ border: '1px solid ' + C.line, borderRadius: 6, marginBottom: 14 }}>
          {threads.length === 0 && <div style={{ padding: 12, color: C.dim }}>No member has written yet.</div>}
          {threads.map((t) => {
            const unread = !((t.readBy || {})[user.uid] && String(t.readBy[user.uid]) >= String(t.lastMessageAt || ''));
            return (
              <button key={t.id} type="button" onClick={() => setOpenUid(t.memberUid)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: openUid === t.memberUid ? C.mine : 'transparent', border: 'none',
                  borderBottom: '1px solid ' + C.line, color: C.text, padding: '8px 12px', cursor: 'pointer' }}>
                <strong>{t.memberName || 'A member'}</strong>{unread && <span style={{ color: C.accent }}> - new</span>}
                <span style={{ color: C.dim, fontSize: 12 }}> · {t.count || 0} message{t.count === 1 ? '' : 's'} · last {when(t.lastMessageAt)} from {sideWords(t.lastFromSide)}</span>
              </button>
            );
          })}
        </div>
      )}

      {(open || !officer) && threads !== null && (
        <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: 12, background: C.panel }}>
          {officer && open && <div style={{ fontSize: 13, color: C.dim, marginBottom: 8 }}>Conversation with {open.memberName}</div>}
          {!open && !officer && <div style={{ color: C.dim, fontSize: 13, marginBottom: 8 }}>No messages yet. Your first message opens the conversation.</div>}
          {(messages || []).map((m) => (
            <div key={m.id} style={{ margin: '8px 0', padding: '8px 10px', borderRadius: 6, background: m.fromUid === user.uid ? C.mine : 'transparent', border: '1px solid ' + C.line }}>
              <div style={{ fontSize: 11, color: C.dim }}>{m.fromName} · {sideWords(m.fromSide)} · {when(m.sentAt)}</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{m.body}</div>
            </div>
          ))}
          {open && <div style={{ fontSize: 11, color: C.dim, margin: '6px 0' }}>{readNames()}</div>}
          {canWrite ? (
            <div style={{ marginTop: 10 }}>
              <textarea aria-label="Your message" className="form-input" rows={4} maxLength={MAX_BODY} value={draft} onChange={(e) => setDraft(e.target.value)}
                placeholder={officer ? 'Your answer' : 'Your message to your Local and General Chairperson'} style={{ width: '100%' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 11, color: C.dim }}>{draft.length} / {MAX_BODY}</span>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={send}>{busy ? 'Sending...' : 'Send'}</button>
              </div>
              {note && <div style={{ color: C.ok, fontSize: 12, marginTop: 6 }}>{note}</div>}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
